import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/storage';
import 'firebase/compat/firestore';
import { UserTier, SystemConfig, HistoryItem, Subtitle, Transaction, TokenUsage, UsageStats } from '../types';
import { PLAN_LIMITS } from '../constants';
import { calculateRawCost } from '../utils';

declare var process: any;

const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID
};

// Initialize Firebase (Compat)
let app: firebase.app.App | null = null;

try {
  // Only initialize if we have at least an API key
  if (firebaseConfig.apiKey && firebaseConfig.apiKey !== 'undefined') {
    if (!firebase.apps.length) {
      app = firebase.initializeApp(firebaseConfig);
    } else {
      app = firebase.app();
    }
  } else {
    console.warn("Firebase config missing or invalid. Features will be disabled. Check your environment variables.");
  }
} catch (e) {
  console.error("Error initializing Firebase:", e);
}

// Export instances - safely check if app exists before calling services
export const auth = app ? firebase.auth() : null;
export const storage = app ? firebase.storage() : null;
export const db = app ? firebase.firestore() : null;

// Only create provider if auth is available to avoid "No Firebase App" error during import
export const googleProvider = (app && auth) ? new firebase.auth.GoogleAuthProvider() : null;

// --- Storage Functions ---
export const uploadFileToFirebase = (
  file: Blob | File, 
  path: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!storage) return reject(new Error("Firebase Storage is not configured."));
    
    const storageRef = storage.ref(path);
    const uploadTask = storageRef.put(file);

    uploadTask.on('state_changed', 
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress);
      }, 
      (error) => {
        reject(error);
      }, 
      async () => {
        const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
        resolve(downloadURL);
      }
    );
  });
};

export const getFileDownloadUrl = async (path: string): Promise<string | null> => {
  if (!storage || !path) return null;
  try {
    const storageRef = storage.ref(path);
    const url = await storageRef.getDownloadURL();
    return url;
  } catch (error) {
    console.warn("Could not retrieve file URL (file might be deleted):", path);
    return null;
  }
};

export const deleteFileFromFirebase = async (path: string) => {
  if (!storage) return;
  try {
    const storageRef = storage.ref(path);
    await storageRef.delete();
  } catch (error: any) {
    if (error.code !== 'storage/object-not-found') {
      console.warn("Error deleting file:", error);
    }
  }
};

// --- Auth & Database Functions ---

const createUserDocIfNotExists = async (user: firebase.User) => {
  if (!db || !user) return;
  try {
    const userRef = db.collection("users").doc(user.uid);
    const userSnap = await userRef.get();
    
    if (!userSnap.exists) {
      await userRef.set({
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        photoURL: user.photoURL,
        credits: 3, // Free trial credits
        tier: 'free', // Default tier
        createdAt: new Date(),
        role: 'user',
        stats: {
          totalInputTokens: 0,
          totalOutputTokens: 0,
          totalRequests: 0,
          estimatedCostUSD: 0
        }
      });
      // Log welcome bonus
      await logTransaction(user.uid, 'bonus', 3, 'Welcome Bonus (Free Trial)');
    }
  } catch (e) {
    console.error("Error creating user doc:", e);
  }
};

export const signInWithGoogle = async () => {
  if (!auth || !googleProvider) throw new Error("Auth not configured");
  try {
    const result = await auth.signInWithPopup(googleProvider);
    if (result.user) {
      await createUserDocIfNotExists(result.user);
    }
    return result.user;
  } catch (error) {
    console.error("Error signing in", error);
    throw error;
  }
};

export const registerWithEmail = async (email: string, pass: string, name: string) => {
  if (!auth) throw new Error("Auth not configured");
  const result = await auth.createUserWithEmailAndPassword(email, pass);
  if (result.user) {
    await result.user.updateProfile({ displayName: name });
    await createUserDocIfNotExists(result.user);
  }
  return result.user;
};

export const loginWithEmail = async (email: string, pass: string) => {
  if (!auth) throw new Error("Auth not configured");
  const result = await auth.signInWithEmailAndPassword(email, pass);
  return result.user;
};

export const logoutUser = async () => {
  if (!auth) return;
  await auth.signOut();
};

export const updateUserProfileData = async (uid: string, data: { displayName?: string, photoURL?: string }) => {
  if (!auth || !db) return;
  const user = auth.currentUser;
  if (!user || user.uid !== uid) throw new Error("Unauthorized");

  // Update Auth Profile
  await user.updateProfile({
    displayName: data.displayName || user.displayName,
    photoURL: data.photoURL || user.photoURL
  });

  // Update Firestore
  await db.collection("users").doc(uid).update(data);
};

export const getUserProfile = async (uid: string) => {
  if (!db) return null;
  try {
    const userRef = db.collection("users").doc(uid);
    const snap = await userRef.get();
    if (snap.exists) {
      return snap.data();
    }
    return null;
  } catch (e) {
    console.error("Error fetching user profile:", e);
    return null;
  }
};

export const getUserCredits = async (uid: string): Promise<number> => {
  const profile = await getUserProfile(uid);
  return profile?.credits || 0;
};

export const deductCredit = async (uid: string) => {
  if (!db) return;
  const userRef = db.collection("users").doc(uid);
  await userRef.update({
    credits: firebase.firestore.FieldValue.increment(-1)
  });
};

// --- Transaction & Plan Management ---

const logTransaction = async (
  uid: string, 
  type: Transaction['type'], 
  amount: number, 
  description: string,
  metadata?: any,
  adminId?: string
) => {
  if (!db) return;
  await db.collection("users").doc(uid).collection("transactions").add({
    type,
    amount,
    description,
    timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    metadata: metadata || {},
    adminId: adminId || null
  });
};

export const purchasePlan = async (uid: string, creditsToAdd: number, newTier: UserTier) => {
  if (!db) return;
  const userRef = db.collection("users").doc(uid);
  
  await userRef.update({
    credits: firebase.firestore.FieldValue.increment(creditsToAdd),
    tier: newTier
  });

  await logTransaction(uid, 'purchase', creditsToAdd, `Purchased ${newTier} plan`);
};

export const getUserTransactions = async (uid: string): Promise<Transaction[]> => {
  if (!db) return [];
  try {
    const snap = await db.collection("users").doc(uid).collection("transactions")
      .orderBy("timestamp", "desc")
      .limit(50)
      .get();
    
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Transaction));
  } catch (e) {
    console.error("Error getting transactions", e);
    return [];
  }
};

// --- History & Stats Management ---

export const saveHistoryItem = async (
  uid: string,
  tier: UserTier,
  data: {
    fileName: string,
    originalName: string,
    storagePath: string,
    subtitles: Subtitle[],
    detectedLanguage: string,
    targetLanguage: string,
    usage?: TokenUsage,
    modelUsed?: string
  }
) => {
  if (!db) return;
  
  // 1. Save History Item
  const retentionDays = PLAN_LIMITS[tier].storageRetentionDays;
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + retentionDays);

  await db.collection("users").doc(uid).collection("history").add({
    ...data,
    uid,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    expiresAt: expiresAt
  });

  // 2. Update Global User Stats (for Admin view of usage)
  if (data.usage) {
      const model = data.modelUsed || 'flash';
      const cost = calculateRawCost(data.usage, model);
      
      // Atomic increment
      await db.collection("users").doc(uid).update({
          "stats.totalInputTokens": firebase.firestore.FieldValue.increment(data.usage.promptTokenCount),
          "stats.totalOutputTokens": firebase.firestore.FieldValue.increment(data.usage.candidatesTokenCount),
          "stats.totalRequests": firebase.firestore.FieldValue.increment(1),
          "stats.estimatedCostUSD": firebase.firestore.FieldValue.increment(cost)
      });
  }
};

export const getUserHistory = async (uid: string): Promise<HistoryItem[]> => {
  if (!db) return [];
  try {
    // Note: The Firestore Rules must allow "list" on this subcollection.
    const snapshot = await db.collection("users").doc(uid).collection("history")
      .orderBy("createdAt", "desc")
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as HistoryItem));
  } catch (error) {
    console.error("Error fetching history for uid:", uid, error);
    return [];
  }
};

export const deleteHistoryItem = async (uid: string, itemId: string, storagePath: string) => {
  if (!db) return;
  
  // 1. Delete from Storage
  if (storagePath) {
    await deleteFileFromFirebase(storagePath);
  }

  // 2. Delete from Firestore
  await db.collection("users").doc(uid).collection("history").doc(itemId).delete();
};

// --- System Configuration (Admin) ---

export const getSystemConfig = async (): Promise<SystemConfig | null> => {
  if (!db) return null;
  try {
    const doc = await db.collection("settings").doc("global").get();
    if (doc.exists) {
      return doc.data() as SystemConfig;
    }
    return null;
  } catch (e) {
    // If settings don't exist or permission denied (e.g. not admin), return null
    return null;
  }
};

export const saveSystemConfig = async (config: SystemConfig) => {
  if (!db) return;
  await db.collection("settings").doc("global").set(config, { merge: true });
};

// --- Admin Functions ---

export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  credits: number;
  role?: string;
  tier?: UserTier;
  createdAt?: any;
  stats?: UsageStats; // New field for real usage tracking
}

export const getAllUsers = async (): Promise<UserData[]> => {
  if (!db) return [];
  try {
    const snapshot = await db.collection("users").get();
    return snapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data()
    } as UserData));
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

export const adminAdjustCredits = async (targetUid: string, amount: number, description: string) => {
  if (!db || !auth) return;
  const adminUser = auth.currentUser;
  
  const userRef = db.collection("users").doc(targetUid);
  await userRef.update({
    credits: firebase.firestore.FieldValue.increment(amount)
  });

  await logTransaction(
    targetUid, 
    amount > 0 ? 'bonus' : 'penalty', 
    amount, 
    description, 
    {}, 
    adminUser?.uid
  );
};

export const adminUpdatePlan = async (targetUid: string, newTier: UserTier) => {
  if (!db || !auth) return;
  const adminUser = auth.currentUser;

  const userRef = db.collection("users").doc(targetUid);
  await userRef.update({ tier: newTier });

  await logTransaction(
    targetUid,
    'plan_change',
    0,
    `Admin changed plan to ${newTier}`,
    { newTier },
    adminUser?.uid
  );
};
